import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";

interface Funnel {
  id: string;
  name: string;
  description: string;
  trigger_type: string;
  is_active: boolean;
  trigger_config?: string;
  webhookUrl?: string;
}

interface FunnelEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  funnel: Funnel | null;
  onSave: (funnelData: any) => void;
}

const FunnelEditModal: React.FC<FunnelEditModalProps> = ({
  open,
  onOpenChange,
  funnel,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    trigger_type: "",
    is_active: true,
    trigger_config: "",
    webhookUrl: "",
  });

  useEffect(() => {
    if (funnel) {
      setFormData({
        name: funnel.name || "",
        description: funnel.description || "",
        trigger_type: funnel.trigger_type || "",
        is_active: funnel.is_active || false,
        trigger_config: funnel.trigger_config || "",
        webhookUrl: funnel.webhookUrl || "",
      });
    }
  }, [funnel]);

  const handleSave = () => {
    onSave({ ...funnel, ...formData });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Sales Funnel</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Funnel Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter funnel name"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the purpose of this funnel..."
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="trigger">Trigger Type</Label>
            <Select
              value={formData.trigger_type}
              onValueChange={(value) => setFormData({ ...formData, trigger_type: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select trigger type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="signup">New User Signup</SelectItem>
                <SelectItem value="profile_complete">Profile Completion</SelectItem>
                <SelectItem value="college_favorite">College Favorited</SelectItem>
                <SelectItem value="subscription_trial">Trial Started</SelectItem>
                <SelectItem value="subscription_expired">Subscription Expired</SelectItem>
                <SelectItem value="manual">Manual Trigger</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="active">Active Status</Label>
              <div className="text-sm text-muted-foreground">
                Enable or disable this funnel
              </div>
            </div>
            <Switch
              id="active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
          </div>

          <Separator />

          <div>
            <Label htmlFor="webhook">Zapier Webhook URL (Optional)</Label>
            <Input
              id="webhook"
              value={formData.webhookUrl}
              onChange={(e) => setFormData({ ...formData, webhookUrl: e.target.value })}
              placeholder="https://hooks.zapier.com/hooks/catch/..."
            />
            <p className="text-sm text-muted-foreground mt-1">
              Connect to Zapier to trigger external automations when this funnel runs
            </p>
          </div>

          <div>
            <Label htmlFor="trigger_config">Trigger Configuration</Label>
            <Textarea
              id="trigger_config"
              value={formData.trigger_config}
              onChange={(e) => setFormData({ ...formData, trigger_config: e.target.value })}
              placeholder="Additional trigger conditions (JSON format)..."
              rows={3}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Changes</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FunnelEditModal;